import React, { useEffect, useRef, useState } from 'react';
import { usePlayer } from '../context/PlayerContext';
import {
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  ChevronUp,
  Heart,
} from 'lucide-react';
import { Slider } from '../components/ui/slider';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MusicPlayer = () => {
  const {
    currentSong,
    isPlaying,
    togglePlay,
    nextSong,
    prevSong,
    setIsPlaying,
    queue,
    currentIndex,
  } = usePlayer();

  const audioRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(80);
  const [isMuted, setIsMuted] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasRecordedPlay, setHasRecordedPlay] = useState(false);

  useEffect(() => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.play().catch(() => setIsPlaying(false));
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying, currentSong]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume / 100;
    }
  }, [volume, isMuted]);

  useEffect(() => {
    setHasRecordedPlay(false);
  }, [currentSong?.id]);

  const recordPlay = async () => {
    if (currentSong && !hasRecordedPlay) {
      try {
        await axios.post(`${API}/songs/${currentSong.id}/play`);
        setHasRecordedPlay(true);
      } catch (error) {
        console.error('Failed to record play:', error);
      }
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
      if (audioRef.current.currentTime > 30 && !hasRecordedPlay) {
        recordPlay();
      }
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) setDuration(audioRef.current.duration);
  };

  const handleSeek = (value) => {
    if (audioRef.current) {
      audioRef.current.currentTime = value[0];
      setCurrentTime(value[0]);
    }
  };

  const handleVolumeChange = (value) => {
    setVolume(value[0]);
    setIsMuted(false);
  };

  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleEnded = () => {
    if (queue.length > 0 && currentIndex < queue.length - 1) {
      nextSong();
    } else {
      setIsPlaying(false);
    }
  };

  if (!currentSong) return null;

  const defaultCover = `https://images.unsplash.com/photo-1581650127213-e72e2271ff15?w=200&h=200&fit=crop`;
  const progressPercent = duration ? (currentTime / duration) * 100 : 0;

  return (
    <>
      <audio
        ref={audioRef}
        src={currentSong.audio_url || currentSong.youtube_url}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
      />

      <div
        className={`fixed left-2 right-2 md:left-4 md:right-4 z-50 transition-all duration-300 ${
          isExpanded ? 'bottom-2 md:bottom-4 top-2 md:top-4' : 'bottom-2 md:bottom-4'
        }`}
        data-testid="music-player"
      >
        <div
          className={`relative bg-[#16181D] border border-[#1E2028] shadow-2xl rounded-2xl backdrop-blur-xl overflow-hidden ${
            isExpanded ? 'h-full' : ''
          }`}
        >
          {/* Top progress line */}
          <div className="absolute top-0 left-0 h-0.5 bg-[#FFE500] transition-all duration-200" style={{ width: `${progressPercent}%` }} />

          {/* Compact View */}
          <div className="flex items-center gap-3 p-3 md:p-4">
            <img
              src={currentSong.cover_image || defaultCover}
              alt={currentSong.title}
              className="h-12 w-12 md:h-14 md:w-14 rounded-xl object-cover flex-shrink-0"
            />

            <div className="min-w-0 flex-1">
              <p className="font-semibold text-white truncate text-sm">{currentSong.title}</p>
              <p className="text-xs text-[#A0A0A0] truncate">{currentSong.artist_name}</p>
            </div>

            <div className="flex items-center gap-1 md:gap-2">
              <button
                onClick={prevSong}
                className="p-2 text-[#A0A0A0] hover:text-white transition-colors hidden sm:block"
                data-testid="prev-btn"
              >
                <SkipBack className="h-5 w-5" />
              </button>

              <button
                onClick={togglePlay}
                className="h-11 w-11 rounded-full bg-[#FFE500] text-black flex items-center justify-center shadow-lg shadow-[#FFE500]/30 hover:scale-105 transition-transform"
                data-testid="play-pause-btn"
              >
                {isPlaying ? <Pause className="h-5 w-5" fill="black" /> : <Play className="h-5 w-5 ml-0.5" fill="black" />}
              </button>

              <button
                onClick={nextSong}
                className="p-2 text-[#A0A0A0] hover:text-white transition-colors hidden sm:block"
                data-testid="next-btn"
              >
                <SkipForward className="h-5 w-5" />
              </button>
            </div>

            {/* Desktop progress */}
            <div className="hidden lg:flex items-center gap-3 flex-1 max-w-sm">
              <span className="text-[11px] text-[#666] w-10 text-right">{formatTime(currentTime)}</span>
              <Slider
                value={[currentTime]}
                max={duration || 100}
                step={1}
                onValueChange={handleSeek}
                className="flex-1"
              />
              <span className="text-[11px] text-[#666] w-10">{formatTime(duration)}</span>
            </div>

            <div className="hidden xl:flex items-center gap-2">
              <button
                onClick={() => setIsMuted(!isMuted)}
                className="p-2 text-[#A0A0A0] hover:text-white transition-colors"
              >
                {isMuted || volume === 0 ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
              </button>
              <Slider
                value={[isMuted ? 0 : volume]}
                max={100}
                step={1}
                onValueChange={handleVolumeChange}
                className="w-20"
              />
            </div>

            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-2 text-[#A0A0A0] hover:text-white transition-colors"
              data-testid="expand-player-btn"
            >
              <ChevronUp className={`h-5 w-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {/* Expanded View */}
          {isExpanded && (
            <div className="flex flex-col items-center justify-center gap-6 p-8 h-[calc(100%-5rem)]">
              <img
                src={currentSong.cover_image || defaultCover}
                alt={currentSong.title}
                className="h-60 w-60 md:h-72 md:w-72 rounded-3xl object-cover shadow-2xl shadow-black/50"
              />
              <div className="text-center">
                <h3 className="text-2xl font-bold text-white">{currentSong.title}</h3>
                <p className="text-[#A0A0A0] mt-1">{currentSong.artist_name}</p>
              </div>

              <div className="w-full max-w-md flex items-center gap-3">
                <span className="text-xs text-[#666]">{formatTime(currentTime)}</span>
                <Slider
                  value={[currentTime]}
                  max={duration || 100}
                  step={1}
                  onValueChange={handleSeek}
                  className="flex-1"
                />
                <span className="text-xs text-[#666]">{formatTime(duration)}</span>
              </div>

              <div className="flex items-center gap-6">
                <button className="text-[#666] hover:text-red-500 transition-colors">
                  <Heart className="h-6 w-6" />
                </button>
                <button onClick={prevSong} className="text-white hover:text-[#FFE500] transition-colors">
                  <SkipBack className="h-7 w-7" />
                </button>
                <button
                  onClick={togglePlay}
                  className="h-16 w-16 rounded-full bg-[#FFE500] flex items-center justify-center shadow-lg shadow-[#FFE500]/30 hover:scale-105 transition-transform"
                >
                  {isPlaying ? <Pause className="h-7 w-7 text-black" fill="black" /> : <Play className="h-7 w-7 text-black ml-1" fill="black" />}
                </button>
                <button onClick={nextSong} className="text-white hover:text-[#FFE500] transition-colors">
                  <SkipForward className="h-7 w-7" />
                </button>
                {queue.length > 0 && (
                  <span className="text-xs text-[#666]">
                    {currentIndex + 1} / {queue.length}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MusicPlayer;
